import { useState } from "react";
import { VStack, Flex, Text, Select, Input, Box, Checkbox, Textarea, Button, Spinner } from "@chakra-ui/react";
import { useGetTestCategories, useGetTestsByCategory, useSaveOrderedTests } from "../../../apis/patients";
import customToast from "../../../components/Toast";

interface TestsProps {
  type: string;
  appointmentId: string;
  patientId: string;
}

interface Test {
  _id: string;
  name: string;
}

const TestCard: React.FC<TestsProps> = ({ type, appointmentId, patientId }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedTests, setSelectedTests] = useState<string[]>([]);
  const [notes, setNotes] = useState<string>("");

  const { data: testCategories, isLoading: isLoadingCategories, error: categoryError } = useGetTestCategories();
  const { data: tests, isLoading: isLoadingTests, error: testsError } = useGetTestsByCategory(selectedCategory);
  const { mutate: saveOrderedTests, isPending } = useSaveOrderedTests();

  const filteredTests: Test[] = (tests || []).filter((test: Test) =>
    test.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const testsAreSelected = selectedCategory && selectedTests.length > 0;
  const notesAreEmpty = notes.trim() === "";
  const isInvalid = !testsAreSelected && notesAreEmpty;

  const handleSubmit = () => {
    if (isInvalid) {
      customToast("Please select at least one test or enter notes.", "error");
      return;
    }

    let formattedTests: { name: string; notes: string }[] = [];

    if (testsAreSelected) {
      formattedTests = selectedTests.map((testId) => {
        const test = tests?.find((t: Test) => t._id === testId);
        return {
          name: test?.name || testId,
          notes,
        };
      });
    } else if (!notesAreEmpty) {
      formattedTests = [{ name: "", notes }];
    }

    saveOrderedTests(
      { appointmentId, patientId, tests: formattedTests },
      {
        onSuccess: () => {
          customToast("Tests ordered successfully!", "success");
          setSelectedTests([]);
          setNotes("");
        },
        onError: (error: any) => {
          customToast(`Error ordering tests: ${error.response?.data?.message || error.message}`, "error");
        },
      }
    );
  };

  return (
    <VStack spacing={6} align="stretch">
      <Flex direction="column" backgroundColor="white" borderRadius="8px" boxShadow="1px 4px 15px 0px #3030301F" width="100%" padding="20px" gap="20px">
      {isLoadingCategories ? (
          <Spinner size="sm" />
        ) : categoryError ? (
          <Text color="red.500">Failed to load categories</Text>
        ) : (
          <Select
            placeholder="Choose a category"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            focusBorderColor="#00B680"
          >
            {testCategories?.map((category: string) => (
              <option key={category} value={category} style={{ color: "black" }}>
                {category}
              </option>
            ))}
          </Select>
        )}

        {selectedCategory && (
          <Input
            placeholder="Search for tests..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            focusBorderColor="#00B680"
            mb="12px"
          />
        )}

        {selectedCategory && (
          <Box>
            <Text fontFamily="lato" fontSize="14px" fontWeight={600} color="gray.800" mb="12px">
              Select Tests
            </Text>

            {isLoadingTests ? (
              <Spinner size="sm" />
            ) : testsError ? (
              <Text color="red.500">Failed to load tests</Text>
            ) : filteredTests.length > 0 ? (
              filteredTests.map((test: Test) => (
                <Flex key={test._id} alignItems="center" justifyContent="space-between" mb="4px">
                  <Text>{test.name}</Text>
                  <Checkbox
                    isChecked={selectedTests.includes(test._id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedTests([...selectedTests, test._id]);
                      } else {
                        setSelectedTests(selectedTests.filter((id) => id !== test._id));
                      }
                    }}
                  >
                    Order
                  </Checkbox>
                </Flex>
              ))
            ) : (
              <Text color="gray.500" fontSize="14px">
                No tests available for this category or matching search.
              </Text>
            )}
          </Box>
        )}
        <Textarea placeholder="Add notes for the selected tests..." value={notes} onChange={(e) => setNotes(e.target.value)} focusBorderColor="#00B680"/>

        <Flex style={{ justifyContent: "flex-end" }}>
          <Button
            colorScheme="green"
            onClick={handleSubmit}
            isLoading={isPending}
            disabled={isPending || isInvalid}
          >
            Submit
          </Button>
        </Flex>
      </Flex>
    </VStack>
  );
};

export default TestCard;
